import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share, SquarePlus, MoreVertical, Check } from 'lucide-react';
import { t } from '../../i18n/index.js';
import { isNativeApp } from '../../lib/native.js';

const DONE_KEY = 'siva.addToHomeSeen';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

/**
 * Is the app already where this step would put it?
 *
 * The native Android app is installed by definition, but a WebView does not report
 * `display-mode: standalone` — so without the first clause, a worker who installed the app would
 * be walked through instructions for adding it to their home screen, inside the app that is
 * already on their home screen.
 */
export function isStandalone(): boolean {
  return (
    isNativeApp() ||
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Whether this device has already been through the step. */
export function addToHomeSeen(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === '1';
  } catch {
    return false;
  }
}

export function markAddToHomeSeen(): void {
  try {
    localStorage.setItem(DONE_KEY, '1');
  } catch {
    /* private mode: the step simply offers itself again next time */
  }
}

type Platform = 'ios' | 'android' | 'other';

/** Which set of instructions actually applies to the phone in the worker's hand. */
export function detectPlatform(ua = navigator.userAgent): Platform {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * The step between setting up a passkey and the punch screen: put the app on the home screen.
 *
 * It matters because a worker who reaches the app through a browser tab has to find the tab, or
 * the link, every morning — and because the app only behaves like an app (full screen, its own
 * icon) once it is installed. Skipping is fine and the step can be reopened from Profile.
 */
export function AddToHomePage() {
  const navigate = useNavigate();
  const [native, setNative] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    // Already running from the home screen: there is nothing to ask for.
    if (isStandalone()) {
      markAddToHomeSeen();
      navigate('/', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setNative(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const proceed = () => {
    markAddToHomeSeen();
    navigate('/', { replace: true });
  };

  const install = async () => {
    if (!native) return;
    await native.prompt();
    const { outcome } = await native.userChoice;
    if (outcome === 'accepted') proceed();
  };

  return (
    <section className="card a2hs" aria-label={t('a2hs_title')}>
      <h2>{t('a2hs_title')}</h2>
      <p>{t('a2hs_why')}</p>

      {installed ? (
        <p className="a2hs-done">
          <Check size={18} aria-hidden="true" /> {t('a2hs_installed')}
        </p>
      ) : platform === 'ios' ? (
        <IosSteps />
      ) : platform === 'android' ? (
        <AndroidSteps native={!!native} onInstall={() => void install()} />
      ) : (
        <ol className="a2hs-steps">
          <li>{t('a2hs_other_1')}</li>
          <li>{t('a2hs_other_2')}</li>
        </ol>
      )}

      <div className="row">
        <button type="button" className={installed ? '' : 'secondary'} onClick={proceed}>
          {installed ? t('a2hs_continue') : t('a2hs_skip')}
        </button>
      </div>
    </section>
  );
}

/** Safari has no install API, so the only honest thing to do is show where the button is. */
function IosSteps() {
  return (
    <>
      <ol className="a2hs-steps">
        <li>
          {t('a2hs_ios_1')}{' '}
          <span className="a2hs-chip">
            <Share size={16} aria-hidden="true" />
          </span>
        </li>
        <li>
          {t('a2hs_ios_2')}{' '}
          <span className="a2hs-chip">
            <SquarePlus size={16} aria-hidden="true" />
          </span>
        </li>
        <li>{t('a2hs_ios_3')}</li>
      </ol>
      <IosIllustration />
    </>
  );
}

function AndroidSteps({ native, onInstall }: { native: boolean; onInstall: () => void }) {
  return (
    <>
      {native && (
        <button type="button" onClick={onInstall} className="a2hs-install">
          {t('a2hs_install')}
        </button>
      )}
      {/* The button is not always offered — Chrome withholds it on a second visit, and other
          Android browsers never fire the event — so the manual route is always shown too. */}
      <p className="muted">{native ? t('a2hs_android_or') : t('a2hs_android_no_button')}</p>
      <ol className="a2hs-steps">
        <li>
          {t('a2hs_android_1')}{' '}
          <span className="a2hs-chip">
            <MoreVertical size={16} aria-hidden="true" />
          </span>
        </li>
        <li>{t('a2hs_android_2')}</li>
      </ol>
    </>
  );
}

/** A phone with the Safari toolbar, pointing at the button the words describe. */
function IosIllustration() {
  return (
    <svg
      className="a2hs-art"
      viewBox="0 0 200 130"
      role="img"
      aria-label={t('a2hs_ios_art_alt')}
    >
      <rect x="52" y="4" width="96" height="112" rx="12" className="a2hs-phone" />
      <rect x="58" y="12" width="84" height="78" rx="6" className="a2hs-screen" />
      <rect x="58" y="94" width="84" height="18" rx="6" className="a2hs-bar" />
      {/* The share button, third along the toolbar. */}
      <g className="a2hs-share">
        <rect x="94" y="97" width="14" height="12" rx="3" />
        <path d="M101 99.5 v5 M98.5 101.5 L101 99 l2.5 2.5" className="a2hs-arrow" />
      </g>
      <path d="M101 122 v-8" className="a2hs-point" />
      <path d="M98 117 l3 4 l3 -4" className="a2hs-point" />
    </svg>
  );
}
